<template>
  <v-container fluid pt-0>
    <v-row align="center" class="pb-2">
      <h1 class="headline">Third Party Setups</h1>
      <v-spacer></v-spacer>
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn v-on="on" fab dark color="primary" @click="onCreateNew">
            <v-icon dark>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>Create Third Party Setup</span>
      </v-tooltip>
    </v-row>
    <v-row>
      <v-col class="pa-0">
        <v-data-table
          :headers="headers"
          :items="items"
          :loading="$apollo.loading"
          item-key="id"
          v-bind="pagingOptions"
          mobile-break-point="1264"
          class="elevation-1"
        >
          <template v-slot:item.inUse="{ item }">
            <v-icon v-if="item.inUse" color="success" small>mdi-check</v-icon>
            <v-icon v-else color="error" small>mdi-close</v-icon>
          </template>
          <template v-slot:item.actions="{ item }">
            <default-list-actions
              :item="item"
              hideView
              @editItem="onEditItem"
              @deleteItem="onDeleteItem"
            >
              <template v-slot:extraButton>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      v-on="on"
                      icon
                      small
                      class="mx-0"
                      @click="editItemContents(item)"
                    >
                      <v-icon color="blue" small>mdi-camera</v-icon>
                    </v-btn>
                  </template>
                  <span>Setup Contents</span>
                </v-tooltip>
              </template>
            </default-list-actions>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <v-dialog
      v-model="itemContentsDialog"
      min-width="1200"
      persistent
      :key="editItem.id"
      scrollable
    >
      <v-card>
        <v-card-title class="grey lighten-3">
          <v-row>
            <v-icon class="draggable">mdi-drag</v-icon>
            <v-col class="py-0">{{ editItem.name }} - Contents</v-col>
          </v-row>
        </v-card-title>
        <v-card-text>
          <contents
            v-if="editItem.id != null"
            :owner-id="editItem.id"
            owner-name="TP_SETUP"
          ></contents>
        </v-card-text>
        <v-card-actions class="grey lighten-4">
          <v-spacer></v-spacer>
          <v-btn color="warning" text @click="itemContentsDialog = false"
            >Close</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <third-party-setup-edit-dialog
      v-model="editedItem"
      :show="showItemEditor"
      @close="showItemEditor = false"
      @input="onSave"
    />
  </v-container>
</template>

<script>
import DefaultListActions from "../../shared/DefaultListActions.vue";
import { ListMixin } from "../../../../mixins/ListMixin";
import ThirdPartySetupEditDialog from "./ThirdPartySetupEditDialog.vue";
import gql from "graphql-tag";
import { removeGQLTypename } from "../../../../helpers";
import Contents from "../../shared/contents/Contents.vue";
const CREATE_MUTATION = gql`
  mutation createThirdPartySetup($input: ThirdPartySetupInput!) {
    createThirdPartySetup(input: $input) {
      id
      name
      label
      level
      inUse
    }
  }
`;
const UPDATE_MUTATION = gql`
  mutation updateThirdPartySetup($input: ThirdPartySetupInput!) {
    updateThirdPartySetup(input: $input) {
      id
      name
      label
      level
      inUse
    }
  }
`;
const DELETE_MUTATION = gql`
  mutation deleteThirdPartySetup($id: ID!) {
    deleteThirdPartySetup(id: $id) {
      id
    }
  }
`;
const LIST_QUERY = gql`
  query allThirdPartySetups {
    thirdPartySetups {
      edges {
        node {
          id
          name
          label
          inUse
          level
          ownerId
          settings {
            edges {
              node {
                id
                name
                content
              }
            }
          }
        }
      }
    }
  }
`;
export default {
  name: "third-party-setups",

  components: { DefaultListActions, ThirdPartySetupEditDialog, Contents },

  mixins: [ListMixin],

  data() {
    return {
      debug: false,
      showItemEditor: false,
      itemContentsDialog: false,
      editedItem: null,
      headers: [
        { text: "Name", value: "name" },
        { text: "Label", value: "label" },
        { text: "Level", value: "level" },
        { text: "InUse", value: "inUse" },
        { text: "Actions", value: "actions", sortable: false },
      ],
    };
  },

  computed: {
    items() {
      return this.thirdPartySetups && this.thirdPartySetups.edges
        ? this.thirdPartySetups.edges
            .map(removeGQLTypename)
            .filter((s) => s.ownerId == "")
        : [];
    },
  },

  methods: {
    editItemContents(item) {
      this.editItem = item;
      this.itemContentsDialog = true;
    },
    onEditItem(item) {
      this.editedItem = item;
      this.showItemEditor = true;
    },
    onDeleteItem(item) {
      this.$apollo
        .mutate({
          mutation: DELETE_MUTATION,
          variables: { id: item.id },
          refetchQueries: [
            {
              query: LIST_QUERY,
            },
          ],
        })
        .then(() => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Third Party Setup Deleted.",
          });
        })
        .catch((err) => {
          const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
        });
    },
    onCreateNew() {
      this.editedItem = null;
      this.showItemEditor = true;
    },
    onSave(payload) {
      //console.log("Save third party setup", payload);
      const mutation = payload.id ? UPDATE_MUTATION : CREATE_MUTATION;
      this.$apollo
        .mutate({
          mutation: mutation,
          variables: { input: { ...payload } },
          refetchQueries: [
            {
              query: LIST_QUERY,
            },
          ],
        })
        .then(() => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Third Party Setup Saved.",
          });
          this.showItemEditor = false;
          this.editedItem = null;
        })
        .catch((err) => {
          const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
        });
    },
  },
  apollo: {
    thirdPartySetups: {
      query: LIST_QUERY,
      fetchPolicy: "network-only",
    },
  },
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/components/gt/plugins/third-party-setups/ThirdPartySetups.vue